import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ExpenseForm } from '@/components/ExpenseForm';
import { ReceiptScanner } from '@/components/ReceiptScanner';
import { normalizeParsedReceipt, type ParsedReceipt } from '@/application/receipt';
import { useAuth } from '@/context/AuthContext';
import { useRepositories } from '@/context/RepositoryContext';
import { invalidateExpenses } from '@/hooks/expensesStore';
import type { ExpenseFormData } from '@/lib/types';

async function readFileAsDataUrl(file: File) {
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Unable to read image.'));
    reader.readAsDataURL(file);
  });
}

export function NewExpenseScanRoute() {
  const { user } = useAuth();
  const { expenseRepository, receiptAiGateway } = useRepositories();
  const navigate = useNavigate();
  const [parsed, setParsed] = useState<ExpenseFormData | null>(null);

  return !parsed ? (
    <ReceiptScanner
      onScan={async (file: File) => {
        const dataUrl = await readFileAsDataUrl(file);
        const text = await receiptAiGateway.parseReceiptImage(dataUrl);
        const cleaned = text.replace(/```json|```/g, '').trim();
        const raw = JSON.parse(cleaned) as ParsedReceipt;
        setParsed(normalizeParsedReceipt(raw));
      }}
    />
  ) : (
    <ExpenseForm
      mode="create"
      initialData={parsed}
      autoExtracted
      onCancel={() => navigate('/expenses')}
      onSubmit={async (data) => {
        if (!user) throw new Error('You must be signed in.');
        await expenseRepository.createExpense({
          user_id: user.uid,
          transaction_date: data.transaction_date,
          store_name: data.store_name || null,
          items: data.items,
          subtotal: data.subtotal,
          tax: data.tax,
          discount: data.discount ?? 0,
          total: data.total,
          status: data.status || 'pending',
          notes: data.notes || null,
          receipt_url: null,
        });
        invalidateExpenses();
        navigate('/expenses');
      }}
    />
  );
}
